import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { Toast, Modal, DetailRow, PageHeader, SearchBar, Pagination, DeleteConfirmModal, BtnSecondary, LoadingSpinner, EmptyState } from './v2-utils';
import '../styles/v2.css';

const PAGE_SIZES = [6, 12, 18];

export default function ViewSuppliersV2() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(6);
  const [selected, setSelected]     = useState(null);
  const [toDelete, setToDelete]     = useState(null);
  const [toast, setToast]           = useState(null);

  useEffect(() => {
    axiosInstance.get('/suppliers')
      .then(r => setSuppliers(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(0); }, [search]);

  const filtered = suppliers.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${s.name||''} ${s.supplier_name||''} ${s.market_name||''} ${s.location||''} ${s.contact_info||''}`.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated  = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/suppliers/${toDelete.supplier_id}`);
      setSuppliers(prev => prev.filter(s => s.supplier_id !== toDelete.supplier_id));
      setToast({ message: 'Supplier deleted.', severity: 'success' });
    } catch {
      setToast({ message: 'Failed to delete supplier.', severity: 'error' });
    } finally { setToDelete(null); }
  };

  const getName = s => s.name || s.supplier_name || s.market_name || 'Unnamed';
  const isMarket = s => !!s.market_name;

  if (loading) return <LoadingSpinner message="Loading suppliers…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Suppliers"
        subtitle={`${filtered.length} record${filtered.length !== 1 ? 's' : ''}`}
        action={
          <button onClick={() => navigate('/new/dashboard/suppliers/add')} className="v2-btn-submit"
            style={{ width: 'auto', padding: '10px 20px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg style={{ width: 15, height: 15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
            Add Supplier
          </button>
        }
      />

      <SearchBar value={search} onChange={setSearch} placeholder="Search by name, location, contact…" />

      {paginated.length === 0 ? (
        <EmptyState message={search ? 'No suppliers match your search.' : 'No suppliers added yet.'} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {paginated.map(s => (
            <div key={s.supplier_id} className="v2-card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: isMarket(s) ? 'linear-gradient(135deg,#fbbf24,#d97706)' : 'linear-gradient(135deg,#60a5fa,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg style={{ width: 20, height: 20, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    {isMarket(s)
                      ? <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                      : <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    }
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getName(s)}</p>
                  <span style={{ fontSize: 11, fontWeight: 600, color: isMarket(s) ? '#92400e' : '#1d4ed8', background: isMarket(s) ? '#fef3c7' : '#eff6ff', padding: '2px 8px', borderRadius: 99, display: 'inline-block', marginTop: 4 }}>
                    {isMarket(s) ? 'Market' : 'Supplier'}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: 13, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {s.contact_info && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg style={{ width: 14, height: 14, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                    {s.contact_info}
                  </div>
                )}
                {s.location && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg style={{ width: 14, height: 14, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                    {s.location}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, paddingTop: 4, borderTop: '1px solid #f1f5f1' }}>
                <button onClick={() => setSelected(s)} style={{ flex: 1, padding: '7px 0', borderRadius: 9, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Details</button>
                <button onClick={() => navigate(`/new/dashboard/suppliers/edit/${s.supplier_id}`)} style={{ flex: 1, padding: '7px 0', borderRadius: 9, border: '1.5px solid #a7f3d0', background: '#f0fdf4', color: '#065f46', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                <button onClick={() => setToDelete(s)} style={{ flex: 1, padding: '7px 0', borderRadius: 9, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={filtered.length}
        onPageChange={setPage} onPageSizeChange={p => { setPageSize(p); setPage(0); }} pageSizes={PAGE_SIZES} />

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Supplier Details"
        footer={<BtnSecondary onClick={() => setSelected(null)}>Close</BtnSecondary>}>
        {selected && <>
          <DetailRow label="Type" value={isMarket(selected) ? 'Market' : 'Supplier'} />
          <DetailRow label="Name" value={getName(selected)} />
          <DetailRow label="Phone" value={selected.contact_info} />
          <DetailRow label="Location" value={selected.location} />
        </>}
      </Modal>

      <DeleteConfirmModal open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={handleDelete}
        itemDescription={toDelete ? getName(toDelete) : ''} />

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
